import React, { useState, useEffect } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { CreditCard, CheckCircle, AlertCircle, ArrowLeft, Shield, Lock } from 'lucide-react';
import { supabase } from '../../lib/supabaseClient';

interface PaymentData {
  id: string;
  amount: number;
  business_id: string;
  booking_id: string;
  product_sale_id?: string;
  booking_number: number;
  business_name: string;
  customer_name: string;
  customer_email: string;
  invoice_total: number;
  amount_paid: number;
  amount_due: number;
  stripe_connect_account_id: string;
}

// Tip options configuration
const tipOptions = [
  { label: 'No Tip', value: 0, percentage: 0 },
  { label: '10%', value: 10, percentage: 10 },
  { label: '15%', value: 15, percentage: 15 },
  { label: '18%', value: 18, percentage: 18 },
  { label: '20%', value: 20, percentage: 20 },
  { label: 'Custom', value: 'custom', percentage: null }
];

export default function PayInvoice() {
  const { token } = useParams<{ token: string }>();
  const [paymentData, setPaymentData] = useState<PaymentData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<any>(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [selectedTipOption, setSelectedTipOption] = useState(tipOptions[0]);
  const [customTipAmount, setCustomTipAmount] = useState('');

  // Calculate tip amount based on selected option
  const calculateTipAmount = () => {
    if (!paymentData) return 0;
    
    const baseAmount = paymentData.amount_due;
    
    if (selectedTipOption.value === 'custom') {
      return parseFloat(customTipAmount) || 0;
    } else {
      return (baseAmount * (selectedTipOption.percentage || 0)) / 100;
    }
  };

  // Calculate total amount including tip
  const calculateTotalAmount = () => {
    if (!paymentData) return 0;
    
    const baseAmount = paymentData.amount_due;
    const tipAmount = calculateTipAmount();
    return baseAmount + tipAmount;
  };

  // Handle tip option change
  const handleTipOptionChange = (option: typeof tipOptions[0]) => {
    setSelectedTipOption(option);
    if (option.value !== 'custom') {
      setCustomTipAmount('');
    }
  };

  // Handle custom tip amount change
  const handleCustomTipChange = (value: string) => {
    setCustomTipAmount(value);
    // Update selected option to custom if user types
    if (value && selectedTipOption.value !== 'custom') {
      setSelectedTipOption(tipOptions[5] as typeof tipOptions[0]); // Custom option
    }
  };

  // Fetch payment details using token
  useEffect(() => {
    const fetchPaymentData = async () => {
      if (!token) {
        setError('Invalid payment link');
        setLoading(false);
        return;
      }

      try {
        console.log('🔍 Fetching payment data for token:', token);

        const { data, error: fetchError } = await supabase
          .from('payments')
          .select(`
            id,
            amount,
            business_id,
            booking_id,
            product_sale_id,
            reference,
            businesses!inner (
              id,
              name,
              stripe_connect_account_id,
              stripe_connect_onboarding_completed,
              stripe_connect_charges_enabled,
              online_payments_enabled
            )
          `)
          .eq('payment_token', token)
          .eq('status', 'pending')
          .single();

        console.log('🔍 DEBUG: Payment data fetched:', data);

        if (fetchError || !data) {
          console.error('Payment not found:', fetchError);
          
          // Capture detailed debug information for display
          const debugDetails = {
            token: token,
            tokenLength: token?.length,
            tokenType: typeof token,
            error: fetchError,
            errorMessage: fetchError?.message,
            errorCode: fetchError?.code,
            errorDetails: fetchError?.details,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            isMobile: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
            url: window.location.href
          };
          
          setDebugInfo(debugDetails);
          setError('Payment link not found or has expired');
          setLoading(false);
          return;
        }

        // Verify Stripe Connect is properly set up
        if (!data.businesses.stripe_connect_account_id || 
            !data.businesses.stripe_connect_onboarding_completed || 
            !data.businesses.stripe_connect_charges_enabled) {
          
          // Capture Stripe Connect debug info
          const stripeDebugInfo = {
            token: token,
            paymentFound: !!data,
            businessId: data?.businesses?.id,
            businessName: data?.businesses?.name,
            stripeAccountId: data?.businesses?.stripe_connect_account_id,
            onboardingCompleted: data?.businesses?.stripe_connect_onboarding_completed,
            chargesEnabled: data?.businesses?.stripe_connect_charges_enabled,
            onlinePaymentsEnabled: data?.businesses?.online_payments_enabled,
            timestamp: new Date().toISOString(),
            userAgent: navigator.userAgent,
            isMobile: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
            url: window.location.href
          };
          
          setDebugInfo(stripeDebugInfo);
          setError('Online payments are not available for this business');
          setLoading(false);
          return;
        }

        // Try to get customer information if booking_id exists
        let customerInfo = {
          email: 'customer@example.com',
          name: 'Customer',
          bookingNumber: 'N/A'
        }

        if (data.booking_id) {
          try {
            console.log('🔍 DEBUG: Attempting to fetch booking with ID:', data.booking_id)
            const { data: booking, error: bookingFetchError } = await supabase
              .from('bookings')
              .select(`
                booking_number,
                customers (
                  full_name,
                  first_name,
                  last_name,
                  email
                )
              `)
              .eq('id', data.booking_id)
              .single()

            console.log('🔍 DEBUG: Booking fetch result:', { booking, bookingFetchError })

            if (booking) {
              customerInfo = {
                email: booking.customers?.email || 'customer@example.com',
                name: booking.customers?.full_name || 
                      `${booking.customers?.first_name || ''} ${booking.customers?.last_name || ''}`.trim() || 'Customer',
                bookingNumber: booking.booking_number || 'N/A'
              }
              console.log('🔍 DEBUG: Final customerInfo:', customerInfo)
            }
          } catch (error) {
            console.log('❌ ERROR: Could not fetch customer info:', error)
          }
        } else {
          console.log('🔍 DEBUG: No booking_id found in payment data:', data.booking_id)
        }

        let bookingData = null;
        let productSaleData = null;
        let amountDue = 0;
        let customerName = 'Customer';
        let invoiceNumber = 'N/A';

        if (data.booking_id) {
          // Handle booking payments (existing logic)
          console.log('🔍 DEBUG: Fetching booking data for booking payment');
          const { data: fetchedBookingData, error: bookingError } = await supabase
            .from('bookings')
            .select(`
              id,
              booking_number,
              total_amount,
              customers (
                first_name,
                last_name
              ),
              payments (
                amount,
                status
              )
            `)
            .eq('id', data.booking_id)
            .single();

          if (bookingError || !fetchedBookingData) {
            console.error('Booking not found:', bookingError);
            setError('Invoice details not found');
            setLoading(false);
            return;
          }

          bookingData = fetchedBookingData;

          // Calculate amounts for booking
          const validPayments = bookingData.payments?.filter((p: any) => 
            p.status === 'completed' || p.status === 'succeeded' || p.status === 'paid'
          ) || [];
          const totalPaid = validPayments.reduce((sum: number, p: any) => sum + p.amount, 0);
          amountDue = Math.max(0, bookingData.total_amount - totalPaid);

          customerName = bookingData.customers 
            ? `${bookingData.customers.first_name} ${bookingData.customers.last_name}`.trim()
            : 'Customer';
          
          invoiceNumber = bookingData.booking_number || 'N/A';

        } else if (data.product_sale_id) {
          // Handle product sale payments - USE DIRECT ID LOOKUP
          console.log('🔍 DEBUG: Fetching product sale data for product payment');
          console.log('🔍 DEBUG: Using product_sale_id:', data.product_sale_id);
          
          const { data: fetchedProductSaleData, error: productSaleError } = await supabase
            .from('product_sales')
            .select(`
              id,
              sale_number,
              total_amount,
              payment_status,
              customers (
                first_name,
                last_name,
                email
              )
            `)
            .eq('id', data.product_sale_id)
            .single();

          if (productSaleError || !fetchedProductSaleData) {
            console.error('Product sale not found:', productSaleError);
            setError('Invoice details not found');
            setLoading(false);
            return;
          }

          productSaleData = fetchedProductSaleData;

            // For product sales, the amount due is the payment amount (since it's already calculated)
            amountDue = data.amount;

            // Check if already paid
            if (productSaleData.payment_status === 'paid') {
              setError('This invoice has already been paid in full');
              setLoading(false);
              return;
            }

            customerName = productSaleData.customers 
              ? `${productSaleData.customers.first_name} ${productSaleData.customers.last_name}`.trim()
              : 'Customer';
            
            invoiceNumber = productSaleData.sale_number || 'N/A';
            
            // Update customerInfo for product sales
            if (productSaleData.customers) {
              customerInfo = {
                email: productSaleData.customers.email || 'customer@example.com',
                name: customerName,
                bookingNumber: productSaleData.sale_number || 'N/A'
              };
            }

        } else {
          // No booking_id or product_sale_id found
          console.error('Payment record has no booking_id or product_sale_id');
          setError('Invoice details not found');
          setLoading(false);
          return;
        }

        if (amountDue <= 0) {
          setError('This invoice has already been paid in full');
          setLoading(false);
          return;
        }

        setPaymentData({
          id: data.id,
          amount: data.amount,
          business_id: data.business_id,
          booking_id: data.booking_id,
          product_sale_id: data.product_sale_id,
          booking_number: parseInt(customerInfo.bookingNumber) || 0,
          business_name: data.businesses.name,
          customer_name: customerInfo.name,
          customer_email: customerInfo.email,
          invoice_total: bookingData ? bookingData.total_amount : (productSaleData ? productSaleData.total_amount : amountDue),
          amount_paid: bookingData ? (bookingData.total_amount - amountDue) : 0,
          amount_due: amountDue,
          stripe_connect_account_id: data.businesses.stripe_connect_account_id
        });



      } catch (err) {
        console.error('Error fetching payment data:', err);
        
        // Capture general error debug info
        const generalDebugInfo = {
          token: token,
          error: err,
          errorMessage: err instanceof Error ? err.message : String(err),
          errorStack: err instanceof Error ? err.stack : undefined,
          timestamp: new Date().toISOString(),
          userAgent: navigator.userAgent,
          isMobile: /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent),
          url: window.location.href
        };
        
        setDebugInfo(generalDebugInfo);
        setError('Failed to load payment details');
      } finally {
        setLoading(false);
      }
    };

    fetchPaymentData();
  }, [token]);

  const handlePayment = async () => {
    if (!paymentData) return;

    const paymentAmount = paymentData.amount_due;
    const tipAmount = calculateTipAmount();
    const totalAmount = calculateTotalAmount();

    // Validate tip amount
    if (tipAmount < 0) {
      alert('Tip amount cannot be negative');
      return;
    }

    try {
      setIsProcessingPayment(true);
      
      console.log('💳 Processing payment:', {
        baseAmount: paymentAmount,
        tipAmount: tipAmount,
        totalAmount: totalAmount,
        businessId: paymentData.business_id,
        accountId: paymentData.stripe_connect_account_id
      });

      // Create Stripe Checkout session
      const { data: checkoutData, error: checkoutError } = await supabase.functions.invoke('create-platform-payment', {
        body: {
          businessId: paymentData.business_id,
          bookingId: paymentData.booking_id,
          totalAmount: Math.round(totalAmount * 100), // Convert to cents
          customerEmail: paymentData.customer_email || 'customer@example.com',
          customerName: paymentData.customer_name || 'Customer',
          description: paymentData.booking_id 
            ? `Payment for Invoice #${paymentData.booking_number}` 
            : `Payment for Product Sale #${paymentData.booking_number}`,
          successUrl: `${window.location.origin}/pay-invoice/${token}/success`,
          cancelUrl: `${window.location.origin}/pay-invoice/${token}`,
          metadata: {
            payment_id: paymentData.id,
            booking_id: paymentData.booking_id,
            product_sale_id: paymentData.product_sale_id,
            payment_token: token,
            base_amount: paymentAmount,
            tip_amount: tipAmount,
            total_amount: totalAmount
          }
        }
      });

      if (checkoutError || !checkoutData?.checkoutUrl) {
        console.error('💥 Payment session creation failed:');
        console.error('  - checkoutError:', checkoutError);
        console.error('  - checkoutData:', checkoutData);
        console.error('  - Request body sent:', {
          businessId: paymentData.business_id,
          bookingId: paymentData.booking_id,
          totalAmount: Math.round(totalAmount * 100),
          customerEmail: paymentData.customer_email,
          customerName: paymentData.customer_name,
          description: paymentData.booking_id 
            ? `Payment for Invoice #${paymentData.booking_number}` 
            : `Payment for Product Sale #${paymentData.booking_number}`,
          successUrl: `${window.location.origin}/pay-invoice/${token}/success`,
          cancelUrl: `${window.location.origin}/pay-invoice/${token}`,
          metadata: {
            payment_id: paymentData.id,
            booking_id: paymentData.booking_id,
            product_sale_id: paymentData.product_sale_id,
            payment_token: token,
            base_amount: paymentAmount,
            tip_amount: tipAmount,
            total_amount: totalAmount
          }
        });
        throw new Error(checkoutError?.message || 'Failed to create payment session');
      }

      console.log('✅ Checkout session created, redirecting...');
      
      // Redirect to Stripe Checkout
      window.location.href = checkoutData.checkoutUrl;

    } catch (error) {
      console.error('Payment error:', error);
      alert('Failed to process payment. Please try again.');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center p-4">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 font-medium">Loading payment details...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-2xl w-full">
          <div className="text-center mb-6">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Not Available</h1>
            <p className="text-gray-600 mb-4">{error}</p>
          </div>
          
          {debugInfo && (
            <div className="bg-gray-50 rounded-lg p-4 mb-6 text-left">
              <h3 className="text-lg font-semibold text-gray-900 mb-3">Debug Information</h3>
              <div className="space-y-2 text-sm">
                <div><strong>Token:</strong> <span className="font-mono bg-white px-2 py-1 rounded">{debugInfo.token || 'null'}</span></div>
                <div><strong>Token Length:</strong> {debugInfo.tokenLength || 'N/A'}</div>
                <div><strong>Is Mobile:</strong> {debugInfo.isMobile ? 'Yes' : 'No'}</div>
                <div><strong>User Agent:</strong> <span className="text-xs break-all">{debugInfo.userAgent}</span></div>
                <div><strong>Current URL:</strong> <span className="text-xs break-all">{debugInfo.url}</span></div>
                <div><strong>Timestamp:</strong> {debugInfo.timestamp}</div>
                
                {debugInfo.error && (
                  <div className="mt-3 p-3 bg-red-50 rounded border">
                    <div><strong>Error Message:</strong> {debugInfo.errorMessage || 'No message'}</div>
                    <div><strong>Error Code:</strong> {debugInfo.errorCode || 'No code'}</div>
                    {debugInfo.errorDetails && (
                      <div><strong>Error Details:</strong> <pre className="text-xs mt-1 whitespace-pre-wrap">{JSON.stringify(debugInfo.errorDetails, null, 2)}</pre></div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
          
          <div className="text-center">
            <button
              onClick={() => window.history.back()}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (paymentSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Payment Successful!</h1>
          <p className="text-gray-600 mb-6">Your payment has been processed successfully. You should receive a confirmation email shortly.</p>
        </div>
      </div>
    );
  }

  // Payment form
  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-blue-50 p-4">
      <div className="max-w-2xl mx-auto pt-8">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-purple-600 to-blue-600 px-8 py-6 text-white">
            <h1 className="text-2xl font-bold">Invoice Payment</h1>
            <p className="text-purple-100 mt-1">Secure online payment</p>
          </div>
          
          <div className="p-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Invoice Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Invoice #:</span>
                    <span className="font-medium">{paymentData?.booking_number}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Business:</span>
                    <span className="font-medium">{paymentData?.business_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Customer:</span>
                    <span className="font-medium">{paymentData?.customer_name}</span>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Payment Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Invoice Total:</span>
                    <span className="font-medium">${paymentData?.invoice_total.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Amount Paid:</span>
                    <span className="font-medium text-green-600">${paymentData?.amount_paid.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-gray-900 font-semibold">Amount Due:</span>
                    <span className="font-bold text-red-600">${paymentData?.amount_due.toFixed(2)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="flex items-center mb-6">
            <CreditCard className="w-6 h-6 text-purple-600 mr-3" />
            <h2 className="text-xl font-bold text-gray-900">Make a Payment</h2>
          </div>

          {/* Payment Amount - Read Only */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Payment Amount
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
              <input
                type="text"
                value={paymentData?.amount_due.toFixed(2) || '0.00'}
                readOnly
                className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-lg font-medium text-gray-900 cursor-not-allowed"
                placeholder="0.00"
              />
            </div>
            <div className="mt-2">
              <p className="text-sm text-gray-600">
                Full invoice amount due
              </p>
            </div>
          </div>

          {/* Tip Section */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Add a Tip (Optional)
            </label>
            
            {/* Tip Options */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              {tipOptions.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleTipOptionChange(option)}
                  className={`py-2 px-3 text-sm font-medium rounded-lg border transition-colors ${
                    selectedTipOption.value === option.value
                      ? 'bg-purple-600 text-white border-purple-600'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {/* Custom Tip Input */}
            {selectedTipOption.value === 'custom' && (
              <div className="relative">
                <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">$</span>
                <input
                  type="number"
                  value={customTipAmount}
                  onChange={(e) => handleCustomTipChange(e.target.value)}
                  min="0"
                  step="0.01"
                  className="w-full pl-8 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  placeholder="Enter custom tip amount"
                />
              </div>
            )}
          </div>

          {/* Payment Summary */}
          <div className="mb-6 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-3">Payment Summary</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Base Amount:</span>
                <span className="font-medium">${paymentData?.amount_due.toFixed(2) || '0.00'}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Tip Amount:</span>
                <span className="font-medium text-green-600">${calculateTipAmount().toFixed(2)}</span>
              </div>
              <div className="flex justify-between border-t pt-2">
                <span className="text-gray-900 font-semibold">Total:</span>
                <span className="font-bold text-purple-600">${calculateTotalAmount().toFixed(2)}</span>
              </div>
            </div>
          </div>

          <button
            onClick={handlePayment}
            disabled={isProcessingPayment}
            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 text-white py-4 px-6 rounded-lg font-semibold text-lg hover:from-purple-700 hover:to-blue-700 focus:outline-none focus:ring-4 focus:ring-purple-300 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
          >
            {isProcessingPayment ? (
              <div className="flex items-center justify-center">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                Processing...
              </div>
            ) : (
              `Pay $${calculateTotalAmount().toFixed(2)} Securely`
            )}
          </button>

          {/* Security badges */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="flex items-center justify-center space-x-6 text-sm text-gray-600">
              <div className="flex items-center">
                <Shield className="w-4 h-4 mr-1" />
                <span>Stripe Secure</span>
              </div>
              <div className="flex items-center">
                <Lock className="w-4 h-4 mr-1" />
                <span>256-bit SSL</span>
              </div>
              <div className="flex items-center">
                <CreditCard className="w-4 h-4 mr-1" />
                <span>All Cards Accepted</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
